import { useCallback, useEffect, useState } from "react";

function useLocalList(key: string) {
  const [list, setList] = useState<string[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setList(parsed);
      }
    } catch {}
    setHydrated(true);
  }, [key]);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(key, JSON.stringify(list));
    } catch {}
  }, [key, list, hydrated]);

  const toggle = useCallback((id: string) => {
    setList((cur) => (cur.includes(id) ? cur.filter((i) => i !== id) : [...cur, id]));
  }, []);

  const has = useCallback((id: string) => list.includes(id), [list]);

  return { list, toggle, has, clear: () => setList([]) };
}

export const useWishlist = () => useLocalList("ns-wishlist-v1");
export const useCompare = () => useLocalList("ns-compare-v1");

/** Small capped list of ids/strings, newest first. Used for recently viewed and search history. */
function useRecentList(key: string, limit: number) {
  const [list, setList] = useState<string[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setList(parsed.slice(0, limit));
      }
    } catch {}
    setHydrated(true);
  }, [key, limit]);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(key, JSON.stringify(list));
    } catch {}
  }, [key, list, hydrated]);

  const push = useCallback(
    (value: string) => {
      const v = value.trim();
      if (!v) return;
      setList((cur) => [v, ...cur.filter((i) => i !== v)].slice(0, limit));
    },
    [limit],
  );

  const removeItem = useCallback((value: string) => setList((cur) => cur.filter((i) => i !== value)), []);

  return { list, push, remove: removeItem, clear: () => setList([]), hydrated };
}

export const useRecentlyViewed = () => useRecentList("ns-recently-viewed-v1", 12);
export const useSearchHistory = () => useRecentList("ns-search-history-v1", 6);
